import { Component, EventEmitter, inject, Input, Output, SimpleChanges, ViewContainerRef } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Record, RecordAvailability } from 'src/app/models/record';
import { ButtonsConfiguration } from 'src/app/models/buttons-config';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { FromScreen } from 'src/app/models/from-screen';
import { HomeComponent } from '../home/home.component';
import { InfoComponent } from '../info/info.component';
import { ResultsComponent } from '../results/results.component';
import { UserHoldsComponent } from '../user/user-holds/user-holds.component';
import { UserComponent } from '../user/user.component';
import { UserCheckoutsComponent } from '../user/user-checkouts/user-checkouts.component';
import { UserFavoritesComponent } from '../user/user-favorites/user-favorites.component';
import { DatePipe } from '@angular/common';
import { Util } from 'src/app/util';
import { LocaleService } from 'src/app/services/locale.service';
import { ConfigService } from 'src/app/services/config.service';
import { PublicListComponent } from '../public-list/public-list.component';
import { Checkout } from 'src/app/state/user/checkouts/models/checkout';
import { Action, ActionType } from 'src/app/models/action';
import { AnalyticsService } from '../../services/analytics.service';
import { UserListsComponent } from '../user/user-lists/user-lists.component';
import { RssItem } from 'src/app/models/rss';
import { RssAllItemsComponent } from '../rss-all-items/rss-all-items.component';
import { UrlStateService } from 'src/app/services/url-state.service';
import { BookclubHistoryApiService } from '../../services/api/bookclub-history-api.service';
import { HoldsFacade } from 'src/app/state/user/holds/holds.facade';
import { CheckoutsFacade } from 'src/app/state/user/checkouts/checkouts.facade';
import { FavoritesFacade } from 'src/app/state/user/favorites/favorites.facade';
import { LoginDialogComponent } from '../../login/components/login-dialog/login-dialog.component';
import { ToastService } from '../../_core/services/toast.service';
import { ExternalVideoDialogFacade } from 'src/app/_core/state/dialogs/external-video/external-video.facade';
import { RatingDialogFacade } from 'src/app/_core/state/dialogs/rating/rating.facade';
import { BookclubLinksDialogFacade } from 'src/app/_core/state/dialogs/bookclub-links/bookclub-links.facade';

@Component({
  template: '',
  styleUrls: ['./base-button.scss'],
})
export class BaseButtonComponent {
  protected readonly toastService = inject(ToastService);
  protected readonly externalVideoDialogFacade = inject(ExternalVideoDialogFacade);
  protected readonly ratingDialogFacade = inject(RatingDialogFacade);
  protected readonly bookclubLinksDialogFacade = inject(BookclubLinksDialogFacade);

  @Input()
  record: Record;

  @Input()
  issueDate: string;

  @Input()
  rssItem: RssItem;

  @Input()
  config: ButtonsConfiguration;

  @Input()
  from: FromScreen;

  @Output()
  renewed = new EventEmitter<Checkout>();

  waiting: boolean;
  recordIsImage: boolean;
  recordIsParentContainer: boolean;
  rhId: string;
  availability: RecordAvailability;
  textDirection: string;
  customSignUpUrl: string;

  constructor(
    public authService: AuthService,
    public userService: UserService,
    public configService: ConfigService,
    protected dialog: MatDialog,
    protected route: ActivatedRoute,
    protected router: Router,
    protected apiService: ApiService,
    protected broadcastService: BroadcastService,
    protected localeService: LocaleService,
    protected datePipe: DatePipe,
    protected analyticsService: AnalyticsService,
    private urlStateService: UrlStateService,
    protected bookClubHistoryService: BookclubHistoryApiService,
    protected viewContainerRef: ViewContainerRef,
    protected holdsFacade: HoldsFacade,
    protected checkoutsFacade: CheckoutsFacade,
    protected favoriteFacade: FavoritesFacade,
  ) {
    this.textDirection = localeService.getTextDirection();
    this.customSignUpUrl = this.configService.get().customSignUpUrl;
  }

  isSubscription() {
    if (!this.record) {
      return false;
    }

    return this.record.availableIssueDates && this.record.availableIssueDates.length;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.record && changes.record.currentValue) {
      this.recordIsImage = this.record.resourceTypes && this.record.resourceTypes.includes('TIPO_IMAGEN');
    }
    if ((changes.record && changes.record.currentValue) || changes.rssItem) {
      this.recordIsParentContainer = this.record.container && !this.rssItem;
      this.rhId = this.rssItem ? this.rssItem.rhId : null;
    }
  }

  ngDoCheck() {
    if (!this.record) {
      return;
    }

    if (this.rssItem) {
      this.availability = this.rssItem.availability;
    } else {
      this.availability = this.record.availability;
    }
  }

  getIssueDates(): string[] {
    return this.record && Util.getIssueDates(this.record);
  }

  doAction(callback: any, action: Action = null, updateAvailability = false) {
    this.waiting = true;

    if (this.authService.isAuthenticated()) {
      callback();

      if (updateAvailability) {
        this.updateAvailability();
      }

      return;
    }

    const customLoginUrl = this.configService.get().customLoginUrl;
    const previousAction = Util.getAction();
    if (action) {
      Util.setAction(action);
    }

    if (customLoginUrl) {
      this.authService.getExternalLoginUrl(customLoginUrl).subscribe((url) => {
        window.location.href = url;
      });
    } else {
      this.dialog
        .open(LoginDialogComponent)
        .afterClosed()
        .subscribe((value) => {
          if (value) {
            this.apiService.getRecord(this.record.id).subscribe((record) => {
              if (record) {
                this.record.externalLink = record.externalLink;
                callback();

                if (updateAvailability) {
                  this.updateAvailability();
                }

                (window as any).location.reload();
              }
            });
          } else {
            this.waiting = false;
            if (previousAction) {
              Util.setAction({
                type: ActionType.NONE,
                recordId: this.record.id,
              });
            } else {
              Util.removeAction();
            }
          }
        });
    }
  }

  getCurrentScreen(): FromScreen {
    if (!this.route.routeConfig) {
      return 'UNKNOWN_SCREEN';
    }
    switch (this.route.routeConfig.component) {
      case HomeComponent:
        return 'INIT_SCREEN';
      case InfoComponent:
        return 'RECORD_SCREEN';
      case ResultsComponent:
        return 'RESULT_SCREEN';
      case UserHoldsComponent:
        return 'USER_HOLD_SCREEN';
      case UserComponent:
      case UserCheckoutsComponent:
      case UserFavoritesComponent:
      case UserListsComponent:
      case PublicListComponent:
      case RssAllItemsComponent:
        return 'USER_SCREENS';
      case PublicListComponent:
        return 'LIST_SCREEN';
      default:
        return 'UNKNOWN_SCREEN';
    }
  }

  onRenewal(renewal: Checkout) {
    this.renewed.emit(renewal);
  }

  isParentContainer() {
    return this.record && this.record.containerData && !this.rssItem;
  }

  protected updateAvailability() {
    if (!this.record) {
      return;
    }

    this.apiService
      .getRecordAvailability(this.record.id, this.userService.getUserId(), this.issueDate, this.rhId)
      .subscribe(
        (availability) => {
          if (this.rssItem && this.rssItem.availability) {
            this.rssItem.availability = availability;
            return;
          }
          if (this.record.availability) {
            this.record.availability = availability;
          }
        },
        (error) => (this.record.active = error.error.ERROR !== 'ERROR_DISCHARGED')
      );
  }
}
